import styled, { css, keyframes } from 'styled-components';
import { Divider, Icon, Link } from '@amzn/storm-ui';

const easeCurve = `cubic-bezier(0.2, 0, 0.38, 0.9)`;

const fadeIn = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 100%;
  }
`;

const reducedMotionMediaQuery = css`
  @media screen and (prefers-reduced-motion: reduce) {
    & {
      transition: none !important;
    }
  }
`;

export const HiddenContent = styled.span`
  opacity: 0;
  width: 100%;
`;

export const ToolbarWrapper = styled.div<{ isOpen: boolean }>`
  background: ${({ theme }) => theme.palette.gray[10]};
  box-sizing: border-box;
  height: 100%;
  overflow: hidden;
  width: 60px;
  padding: ${({ theme }) => theme.spacing.small} 0;
  padding-inline-end: 5px;
  box-shadow:
    0 0 1px 0 rgba(0, 0, 0, 0.1),
    1px 0 5px 0 rgba(0, 0, 0, 0.1);
  z-index: 300;

  &:hover {
    overflow: hidden auto;
  }

  ${reducedMotionMediaQuery};
  -webkit-transition: all 100ms linear;
  -moz-transition: all 100ms linear;
  -o-transition: all 100ms linear;
  -ms-transition: all 100ms linear;
  transition: all 100ms linear;

  ${({ isOpen }) =>
    isOpen &&
    css`
      width: 310px;

      & ${HiddenContent} {
        opacity: 1;
        animation: ${fadeIn} 0.15s linear;
      }
    `}
`;

export const Overlay = styled.div`
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 60px;
  right: 0;
  bottom: 0;
  background-color: ${({ theme }) => theme.palette.inkwell};
  z-index: 51;
  opacity: 10%;
`;

const ActiveSectionIndicator = css`
  ::before {
    content: '';
    height: 32px;
    width: 3px;
    background-color: ${({ theme }) => theme.palette.orange[600]};
    position: absolute;
    top: 4px;
    left: 0;
  }
`;

export const SideNavLinkOuterWrapper = styled.div<{ isSelected: boolean }>`
  min-height: 40px;
  width: 310px;
  cursor: pointer;
  margin-bottom: ${({ theme }) => theme.spacing.small};
  padding-inline: ${({ theme }) => theme.spacing.small};
  position: relative;
  box-sizing: border-box;
  ${({ isSelected }) => isSelected && ActiveSectionIndicator}
`;

const sideNavLinkStyles = css`
  width: 100%;
  display: flex;
  align-items: center;
  font-size: 13px;
  line-height: 19px;
  color: ${({ theme }) => theme.palette.inkwell};
  text-decoration: none;
  border-radius: 5px;
  cursor: pointer;
  border: none;
  background: ${({ theme }) => theme.palette.gray[10]};
  height: 40px;

  &:hover {
    background-color: ${({ theme }) => theme.palette.gray[100]};
  }

  &:active svg {
    transform: scale(0.95);
    transition-duration: 0.1s;
    ${reducedMotionMediaQuery};
  }

  &:focus {
    outline: none;
  }
`;

export const SideNavLinkInnerWrapper = styled(Link)<{
  isSelected: boolean;
  isNavOpen: boolean;
}>`
  ${sideNavLinkStyles}
  &:link,
  &:visited {
    color: ${({ theme }) => theme.palette.inkwell};
    text-decoration: none;
  }

  ${({ isSelected }) =>
    isSelected &&
    css`
      font-weight: 700;
    `}

  ${({ isNavOpen }) =>
    isNavOpen &&
    css`
      &:focus {
        background-color: ${({ theme }) => theme.palette.gray[100]};
        outline: 2px solid ${({ theme }) => theme.palette.blue[700]};
        outline-offset: 2px;
      }
    `}
`;

export const SideNavLinkTitleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-inline-end: 12px;
`;

export const SvgWrapper = styled.div<{ isSelected: boolean }>`
  box-sizing: unset;
  min-width: 40px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin-inline-end: ${({ theme }) => theme.spacing.micro};

  ${({ isSelected }) =>
    !isSelected &&
    css`
      path,
      rect {
        fill: ${({ theme }) => theme.palette.gray[900]};
      }
    `};
`;

export const AccordionWrapper = styled.div<{ isActive: boolean }>`
  width: 310px;
  margin-bottom: ${({ theme }) => theme.spacing.small};
`;

export const AccordionButtonOuterWrapper = styled.div<{
  isSelected: boolean;
}>`
  padding-inline-end: ${({ theme }) => theme.spacing.small};
  padding-inline-start: ${({ theme }) => theme.spacing.small};
  position: relative;
  ${({ isSelected }) => isSelected && ActiveSectionIndicator}
`;

export const AccordionButtonInnerWrapper = styled.button<{
  isSelected: boolean;
  isExpanded: boolean;
  isNavOpen: boolean;
}>`
  padding-inline: 0 12px;
  text-align: initial;

  ${sideNavLinkStyles}
  ${({ isSelected, isExpanded }) =>
    (isSelected || isExpanded) &&
    css`
      font-weight: 700;
    `}
  ${({ isNavOpen }) =>
    isNavOpen &&
    css`
      &:focus {
        background-color: ${({ theme }) => theme.palette.gray[100]};
        outline: 2px solid ${({ theme }) => theme.palette.blue[700]};
        outline-offset: 2px;
      }
    `}
  :focus:not(:focus-visible) {
    outline: 0;
  }
`;

export const AccordionDropdownIcon = styled(Icon)`
  font-size: 14px;
  margin-inline-start: auto;
`;

export const AccordionContentOuterWrapper = styled.div<{ isActive: boolean }>`
  overflow: hidden;
  padding-inline-start: 29px;
  padding-inline-end: ${({ theme }) => theme.spacing.small};
  ${reducedMotionMediaQuery};
  transition: max-height 0.1s ${easeCurve};
  max-height: 0;

  ${({ isActive }) =>
    isActive &&
    css`
      transition: max-height 0.25s ${easeCurve};
      overflow: initial;
    `}
`;

export const AccordionContentInnerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: ${({ theme }) => theme.spacing.small};
  border-inline-start: 2px solid ${({ theme }) => theme.palette.gray[200]};
`;

export const AccordionContentGroupWrapper = styled(HiddenContent)`
  display: flex;
  flex-direction: column;
  padding-inline-start: ${({ theme }) => theme.spacing.base};
  box-sizing: border-box;
`;

export const AccordionContentSubTitle = styled.span`
  color: rgb(35, 47, 63);

  &&& {
    font-size: 12px;
  }

  font-weight: bold;
  padding: ${({ theme }) => theme.spacing.base} 12px
    ${({ theme }) => theme.spacing.micro};
  line-height: 1.333;
  margin-bottom: 3px;
`;

export const AccordionLinkFeaturesWrapper = styled.div<{
  isLandingPage: boolean;
}>`
  display: flex;
  align-items: center;
  margin-inline-end: 12px;
  ${({ isLandingPage }) =>
    isLandingPage &&
    css`
      margin-inline-end: 0;
    `}
`;

export const AccordionLinkWrapper = styled.div<{
  isSelected: boolean;
  isLandingPageIconDisplayed: boolean;
}>`
  display: flex;
  width: 100%;
  justify-content: space-between;
  height: 40px;
  border-radius: 5px;
  margin-bottom: 3px;

  ${({ isSelected, theme }) =>
    isSelected &&
    css`
      background-color: ${theme.palette.gray[50]};
    `}
  &:hover, &:focus {
    background-color: ${({ theme }) => theme.palette.gray[100]};
  }

  [id^='aac-set-landing-nav'] {
    display: none;
  }

  &&&:hover,
  &&&:focus-within {
    [id^='aac-set-landing-nav'] {
      display: inline-block;
    }
  }

  ${({ isLandingPageIconDisplayed }) =>
    isLandingPageIconDisplayed &&
    css`
      &&&:hover
        ${AccordionLinkFeaturesWrapper},
        &&&:focus-within
        ${AccordionLinkFeaturesWrapper} {
        margin-inline-end: 0;
      }
    `}
`;

export const AccordionLink = styled(Link)`
  padding: ${({ theme }) => theme.spacing.small} 12px;
  border-radius: 5px;
  font-size: 13px;
  line-height: 20px;
  flex-grow: 1;

  &:link,
  &:visited {
    color: ${({ theme }) => theme.palette.inkwell};
    text-decoration: none;
  }
`;

export const ExternalIcon = styled(Icon)`
  margin-inline: 8px;
`;

export const StyledDivider = styled(Divider)`
  margin: 5px 12px;
`;
